<template>
  <div ref="inputWrapper" class="input-wrapper">
    <div class="relative" :class="!hasFloatLabel && 'flex flex-col-reverse gap-2'">
      <Label
        :for="name"
        :class="[
          'pointer-events-none bg-background px-2 text-sm',
          error && '!text-destructive',
          hasFloatLabel &&
            'absolute start-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform text-black/60 duration-150 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:bg-background/100 peer-focus:px-2 peer-focus:!text-black/100 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4',
        ]"
        >{{ label }}</Label
      >
      <Popover v-model:open="isOpen">
        <div class="w-full">
          <PopoverTrigger as-child>
            <Button
              type="button"
              variant="outline"
              :class="[
                'focus:border-input- flex h-10 w-full justify-start rounded-md border border-input/20 bg-background/40 px-3 py-1 text-left text-sm font-normal shadow-sm ring-0 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium hover:bg-background/100 hover:text-black/60 focus:border-input/100 focus:outline-none focus:ring-0 focus:ring-transparent disabled:cursor-not-allowed disabled:opacity-50',
                errorInput && 'border-destructive',
                !selectedLocation && 'text-muted-foreground',
              ]"
              :disabled="disabled"
            >
              <MapPin class="mr-2 h-4 w-4" color="grey" />
              {{ displayLocation }}
            </Button>
          </PopoverTrigger>
        </div>
        <span v-if="error" class="input-error">{{ error }}</span>
        <PopoverContent
          class="rounded-md border border-input/20 bg-background p-0 shadow-sm transition-colors"
          :style="{ width: `${inputWidth}px` }"
        >
          <div class="grid px-4 pb-8 pt-4">
            <div class="grid gap-y-4">
              <div class="grid gap-y-3">
                <span class="block font-bold">{{ t('input.location.title') }}</span>
                <p class="text-sm opacity-70">
                  {{ t('input.location.description') }}
                </p>
              </div>
              <div class="input-wrapper">
                <Label :for="name">{{ label }}</Label>
                <Combobox by="label">
                  <ComboboxAnchor>
                    <div class="relative w-full items-center">
                      <ComboboxInput
                        v-model="search"
                        class="flex h-10 w-full rounded-md border border-input/20 bg-background/40 pl-9 text-sm shadow-sm ring-0 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus:border-input/100 focus:outline-none focus:ring-0 focus:ring-transparent disabled:cursor-not-allowed disabled:opacity-50"
                        :display-value="(val) => val?.label ?? ''"
                        :placeholder="t('input.location.placeholder')"
                        :class="loadingCurrentLocation && 'pointer-events-none opacity-60'"
                      />
                      <span
                        class="absolute inset-y-0 start-0 flex items-center justify-center px-3"
                      >
                        <Search class="size-4 text-muted-foreground" />
                      </span>
                      <span class="absolute inset-y-0 end-0 flex items-center justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          class="rounded-l-none border-l border-input/20 px-3 hover:bg-black/5"
                          @click="clearLocation"
                        >
                          <X class="h-4 w-4" />
                        </Button>
                      </span>
                    </div>
                  </ComboboxAnchor>

                  <ComboboxList :style="{ width: `${inputWidth}px` }">
                    <ComboboxGroup>
                      <div v-if="isSearching" class="flex w-full items-center justify-center py-4">
                        <Loader2 class="h-5 w-5 animate-spin" />
                      </div>
                      <ComboboxItem
                        v-else
                        v-for="item in items"
                        :key="JSON.stringify(item.location)"
                        :value="item"
                        @click="selectLocation(item)"
                      >
                        {{ item.label }}
                        <ComboboxItemIndicator>
                          <Check :class="cn('ml-auto h-4 w-4')" />
                        </ComboboxItemIndicator>
                      </ComboboxItem>
                      <ComboboxEmpty v-if="!isSearching && !items.length">
                        {{ t('input.location.emptyList') }}
                      </ComboboxEmpty>
                    </ComboboxGroup>
                  </ComboboxList>
                </Combobox>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </div>
  <span class="input-error" v-if="errorInput">{{ errorInput }}</span>
</template>

<script setup lang="ts">
import { cn } from '@lib/utils';
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import type { Location } from '@modules/UserDetailsForm/UserDetails.interface';
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
} from '@ui/combobox';
import Button from '@ui/button/Button.vue';
import { Popover, PopoverTrigger, PopoverContent } from '@ui/popover';
import {
  autoCompleteRadar as autoComplete,
  getPlaceDetailsGoogle,
  reverseGeoCodingRadar as reverseGeoCoding,
} from '@api/geo.api';
import { useI18n } from 'vue-i18n';
import { Check, Search, MapPin, Loader2, X } from 'lucide-vue-next';
import Label from '@ui/label/Label.vue';

interface InputProps {
  label: string;
  placeholder: string;
  name: string;
  type?: string;
  errorInput?: string;
  disabled?: boolean;
  hasFloatLabel?: boolean;
}

const props = withDefaults(defineProps<InputProps>(), {
  type: 'text',
  errorInput: undefined,
  disabled: false,
  hasFloatLabel: true,
});

interface LocationItem {
  label?: string;
  location: [number, number];
  city: string | null;
  state: string | null;
  county: string | null;
  country: string | null;
  countryCode: string | null;
  latitude: number;
  longitude: number;
  type: 'radar' | 'google' | 'autodetect';
}

interface LocationPrediction extends LocationItem {
  place_id?: string;
}

defineEmits(['update:modelValue', 'change', 'input', 'blur']);

const { t } = useI18n();

const error = ref('');
const search = ref('');
const isSearching = ref(false);
const items = ref<LocationItem[]>([]);
const isOpen = ref(false);
const locationServiceEnabled = ref(true);
const selectedLocationPrediction = ref<LocationPrediction | null>(null);
const selectedLocation = ref<LocationPrediction | null>(null);
const loadingCurrentLocation = ref(false);
const inputWrapper = ref<HTMLElement | null>(null);
const inputWidth = ref(0);

const modelValue = defineModel<Location | null>('modelValue', {
  required: true,
});

// Watchers
watch(search, (query) => {
  searchQueryChange(query);
});

watch(selectedLocation, (newVal) => {
  if (newVal === null) {
    modelValue.value = null;
  } else {
    modelValue.value = {
      latitude: newVal.latitude,
      longitude: newVal.longitude,
      city: newVal.city,
      countryCode: newVal.countryCode,
    };
    error.value = '';
  }
});

watch(isOpen, (newVal) => {
  if (!newVal) return;
  getCurrentLocation();
});

// Methods
const searchQueryChange = async (query: string) => {
  if (!query || query.length < 2) {
    items.value = [];
    selectedLocationPrediction.value = null;
    return;
  }

  if (
    !selectedLocationPrediction.value ||
    search.value !== selectedLocationPrediction.value.label
  ) {
    callAutoComplete(search.value);
    return;
  }

  if (selectedLocationPrediction.value?.type === 'radar') {
    selectedLocation.value = { ...selectedLocationPrediction.value };
    return;
  }

  if (
    selectedLocationPrediction.value?.type === 'google' &&
    selectedLocationPrediction.value?.place_id
  ) {
    const location = await getGoogleLocationDetails(selectedLocationPrediction.value.place_id);
    selectedLocation.value = {
      ...location,
      label: selectedLocationPrediction.value.label,
    } as LocationItem;
    return;
  }

  if (
    selectedLocationPrediction.value &&
    ['autodetect', 'autodetect-radar', 'autodetect-google'].includes(
      selectedLocationPrediction.value.type,
    )
  ) {
    selectedLocation.value = { ...selectedLocationPrediction.value };
    return;
  }

  search.value = '';
};

const selectLocation = (location: object) => {
  isOpen.value = false;
  selectedLocation.value = location as LocationPrediction;
};

const clearLocation = () => {
  selectedLocation.value = null;
  // modelValue.value = null;
  search.value = '';
  error.value = '';
};

const callAutoComplete = async (searchTerm: string) => {
  isSearching.value = true;
  try {
    const response = await autoComplete(searchTerm, true);
    if (['google', 'radar'].includes(response.type)) {
      items.value = response.items;
      isSearching.value = false;
    } else {
      items.value = [];
    }
  } catch (error) {
    (error as { value: string }).value = 'Location service error';
    locationServiceEnabled.value = false;
  }
};

const getGoogleLocationDetails = async (place_id: string) => {
  loadingCurrentLocation.value = true;
  try {
    const location = await getPlaceDetailsGoogle(place_id);
    loadingCurrentLocation.value = false;
    return location;
  } catch (error) {
    (error as { value: string }).value = 'Location service error';
    locationServiceEnabled.value = false;
  }
  loadingCurrentLocation.value = false;
  return null;
};

const getCurrentLocation = () => {
  loadingCurrentLocation.value = true;
  error.value = '';

  if (!navigator.geolocation) {
    error.value = t('input.location.geolocationNotSupported');
    loadingCurrentLocation.value = false;
    return;
  }

  const options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };

  navigator.geolocation.getCurrentPosition(
    getCurrentLocationSuccess,
    getCurrentLocationError,
    options,
  );
};

const getCurrentLocationSuccess = async (position: GeolocationPosition) => {
  const coordinates = {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
  };

  let location: LocationPrediction = {
    latitude: coordinates.lat,
    longitude: coordinates.lng,
    label: 'Location Detected',
    city: null,
    state: null,
    country: null,
    county: null,
    countryCode: null,
    location: [coordinates.lat, coordinates.lng],
    type: 'radar',
  };

  try {
    const resp = await reverseGeoCoding(coordinates, true);
    location = {
      ...location,
      latitude: Number(resp.latitude),
      longitude: Number(resp.longitude),
      city: resp.city ?? null,
      country: resp.country ?? null,
      state: resp.state ?? null,
      county: resp.country ?? null,
      countryCode: (resp.countryCode as string) ?? null,
      type: (resp.type as 'radar' | 'google' | 'autodetect') ?? 'autodetect',
      location: [Number(resp.latitude), Number(resp.longitude)],
    };

    if (resp.city && resp.country) {
      location.label = `${resp.city}, ${resp.country}`;
    } else if (resp.city) {
      location.label = resp.city;
    } else if (resp.country) {
      location.label = resp.country;
    } else if (resp.state) {
      location.label = resp.state;
    }
  } catch (error) {
    console.error('Reverse geocoding failed:', error);
  } finally {
    selectedLocationPrediction.value = { ...location };
    items.value = [{ ...location }];
    search.value = location.label ?? '';
    selectedLocation.value = location;
    loadingCurrentLocation.value = false;
  }
};

const getCurrentLocationError = (err: GeolocationPositionError) => {
  if (err && err.code === 1) {
    error.value = t('input.location.failedToDetectLocation');
  } else {
    error.value = t('input.location.locationError');
  }
  loadingCurrentLocation.value = false;
};

const updateWidth = () => {
  if (inputWrapper.value) {
    inputWidth.value = inputWrapper.value.offsetWidth;
  }
};

// Computed
const displayLocation = computed(() => {
  return modelValue.value?.city || selectedLocation?.value?.city || props.placeholder;
});

onMounted(() => {
  const resizeObserver = new ResizeObserver(updateWidth);
  if (inputWrapper.value) {
    resizeObserver.observe(inputWrapper.value);
  }

  updateWidth();
  onBeforeUnmount(() => {
    resizeObserver.disconnect();
  });
});
</script>
